import { z } from "zod";

const DEFAULT_PUBLIC_ERROR =
  "Campaign secure access is temporarily unavailable.";
const INVALID_PUBLIC_ERROR =
  "This Campaign access link is invalid or unavailable.";
const RATE_LIMIT_PUBLIC_ERROR =
  "Too many Campaign access attempts. Please wait and try again.";
const CODE_INVALID_PUBLIC_ERROR =
  "That rehearsal code is invalid. Check the six digits and try again.";
const CHALLENGE_EXPIRED_PUBLIC_ERROR =
  "This rehearsal code has expired. Ask your advisor to revoke it and prepare a new rehearsal link.";
const CHALLENGE_LOCKED_PUBLIC_ERROR =
  "This rehearsal code is locked. Ask your advisor to revoke it and prepare a new rehearsal link.";
const DELIVERY_DISABLED_PUBLIC_ERROR =
  "Provider-disabled OTP rehearsal is not available for this link.";
const DRAFT_CONFLICT_PUBLIC_ERROR =
  "This saved draft changed. Reload the saved draft before trying again.";
const DRAFT_CLEAR_PUBLIC_ERROR =
  "The saved draft could not be cleared. Reload it before trying again.";
const DRAFT_VALIDATION_PUBLIC_ERROR =
  "The synthetic draft value was not accepted. Review it and try again.";
const RESPONSE_CONFLICT_PUBLIC_ERROR =
  "This synthetic response changed. Reloading the active rehearsal state is required.";
const RESPONSE_VALIDATION_PUBLIC_ERROR =
  "The saved synthetic response was not accepted. Update and save it before trying again.";

const LINK_TOKEN_PATTERN = /^crl_v1_[A-Za-z0-9_-]{43}$/;
const UUID_V4_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const LOWERCASE_UUID_V4_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;
const MASKED_TEST_DESTINATION = /^[a-z0-9]\*{3}@[a-z0-9]\*{3}\.test$/i;
const MASKED_DESTINATION =
  /^[^@\s*]\*{3}@[^@\s*]\*{3}(?:\.[A-Za-z0-9-]{1,63})+$/;
const csrfTokenSchema = z.string().regex(/^[A-Za-z0-9_-]{43}$/);
const campaignQuestionRefSchema = z.string().regex(/^cq_v1_[A-Za-z0-9_-]{43}$/);
const campaignDraftRequestTokenSchema = z.string().regex(/^cdt_v1_[A-Za-z0-9_-]{43}$/);
const campaignQuestionRefV2Schema = z.string().regex(/^cq_v2_[A-Za-z0-9_-]{43}$/);
const campaignOptionRefSchema = z.string().regex(/^co_v1_[A-Za-z0-9_-]{43}$/);
const campaignAnswerSetRefSchema = z.string().regex(/^cas_v1_[A-Za-z0-9_-]{43}$/);
const campaignDraftRequestTokenV2Schema = z.string().regex(/^cdt_v2_[A-Za-z0-9_-]{43}$/);
const RESPONSE_TYPES = [
  "text",
  "textarea",
  "number",
  "currency",
  "percent",
  "date",
  "dropdown",
  "multiselect",
  "checkbox",
  "radio",
  "file",
];
const CHOICE_RESPONSE_TYPES = new Set(["dropdown", "multiselect", "radio"]);
const MAX_QUESTIONNAIRE_PREVIEW_BYTES = 1024 * 1024;
const MAX_DRAFT_VALUE_SCALARS = 200;
const MAX_DRAFT_VALUE_UTF8_BYTES = 800;
const MAX_MULTI_ANSWER_CANONICAL_BYTES = 14_000;
const MAX_MULTI_ANSWER_REQUEST_BYTES = 24 * 1024;

const containsUnpairedSurrogate = (value) => {
  for (let index = 0; index < value.length; index += 1) {
    const codeUnit = value.charCodeAt(index);
    if (codeUnit >= 0xd800 && codeUnit <= 0xdbff) {
      const next = value.charCodeAt(index + 1);
      if (!(next >= 0xdc00 && next <= 0xdfff)) return true;
      index += 1;
    } else if (codeUnit >= 0xdc00 && codeUnit <= 0xdfff) {
      return true;
    }
  }
  return false;
};

const containsDisallowedDraftText = (value) => {
  for (const character of value) {
    const codePoint = character.codePointAt(0);
    if (
      (codePoint >= 0 && codePoint <= 0x1f)
      || (codePoint >= 0x7f && codePoint <= 0x9f)
      || codePoint === 0x061c
      || codePoint === 0x200e
      || codePoint === 0x200f
      || codePoint === 0x2028
      || codePoint === 0x2029
      || (codePoint >= 0x202a && codePoint <= 0x202e)
      || (codePoint >= 0x2066 && codePoint <= 0x2069)
    ) return true;
  }
  return false;
};

const campaignDraftTextSchema = z.string()
  .refine((value) => !containsUnpairedSurrogate(value))
  .refine((value) => value.normalize("NFC") === value)
  .refine((value) => value === value.trim())
  .refine((value) => !containsDisallowedDraftText(value))
  .refine((value) => Array.from(value).length >= 1)
  .refine((value) => Array.from(value).length <= MAX_DRAFT_VALUE_SCALARS)
  .refine((value) => utf8ByteLength(value) <= MAX_DRAFT_VALUE_UTF8_BYTES);

const containsDisallowedDisplayText = (value) => {
  for (const character of value) {
    const codePoint = character.codePointAt(0);
    if (
      (codePoint >= 0 && codePoint <= 8)
      || codePoint === 11
      || codePoint === 12
      || (codePoint >= 14 && codePoint <= 31)
      || codePoint === 127
      || (codePoint >= 0x202a && codePoint <= 0x202e)
      || (codePoint >= 0x2066 && codePoint <= 0x2069)
    ) return true;
  }
  return false;
};

const canonicalDisplayText = (maxLength) => z
  .string()
  .min(1)
  .max(maxLength)
  .refine((value) => value === value.trim())
  .refine((value) => !containsDisallowedDisplayText(value));

const nullableDisplayText = (maxLength) => z.union([
  z.null(),
  canonicalDisplayText(maxLength),
]);

function utf8ByteLength(value) {
  let bytes = 0;
  for (const character of value) {
    const codePoint = character.codePointAt(0);
    if (codePoint <= 0x7f) bytes += 1;
    else if (codePoint <= 0x7ff) bytes += 2;
    else if (codePoint <= 0xffff) bytes += 3;
    else bytes += 4;
  }
  return bytes;
}

const sequencedArray = (schema, maximum) => z
  .array(schema)
  .max(maximum)
  .superRefine((values, context) => {
    values.forEach((value, index) => {
      if (value.sequence !== index + 1) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: [index, "sequence"],
          message: "Noncanonical display sequence",
        });
      }
    });
  });

const questionnaireOptionSchema = z.object({
  sequence: z.number().int().positive().safe(),
  label: canonicalDisplayText(500),
}).strict();

const questionnaireQuestionSchema = z.object({
  sequence: z.number().int().positive().safe(),
  label: canonicalDisplayText(500),
  helpText: nullableDisplayText(2000),
  responseType: z.enum(RESPONSE_TYPES),
  required: z.boolean(),
  conditionalRequired: z.boolean(),
  conditionalVisibility: z.boolean(),
  options: sequencedArray(questionnaireOptionSchema, 100),
}).strict().superRefine((question, context) => {
  const requiresOptions = CHOICE_RESPONSE_TYPES.has(question.responseType);
  const permitsOptions = requiresOptions || question.responseType === "checkbox";
  if (requiresOptions && question.options.length === 0) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["options"],
      message: "Choice display options required",
    });
  }
  if (!permitsOptions && question.options.length > 0) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["options"],
      message: "Display options are not allowed for this response type",
    });
  }
});

const questionnaireSectionSchema = z.object({
  sequence: z.number().int().positive().safe(),
  title: canonicalDisplayText(300),
  description: nullableDisplayText(2000),
  questions: sequencedArray(questionnaireQuestionSchema, 100).refine(
    (questions) => questions.length > 0,
  ),
}).strict();

const localInteractionSchema = z.object({
  schemaVersion: z.literal("campaign_uat_public_local_interaction.v1"),
  mode: z.literal("client_memory_text_only"),
  allowed: z.boolean(),
  supportedResponseTypes: z.tuple([z.literal("text")]),
  maxCharacters: z.literal(200),
  answerTransmissionAllowed: z.literal(false),
  answerPersistenceAllowed: z.literal(false),
  browserStorageAllowed: z.literal(false),
  telemetryAllowed: z.literal(false),
}).strict();

const publicQuestionnairePreviewV2Schema = z.object({
  schemaVersion: z.literal("campaign_uat_public_questionnaire_preview.v2"),
  source: z.literal("immutable_blueprint_run_snapshot"),
  questionnaire: z.object({
    name: canonicalDisplayText(300),
    version: z.number().int().positive().safe(),
    sections: sequencedArray(questionnaireSectionSchema, 50).refine(
      (sections) => sections.length > 0,
    ),
  }).strict(),
  capabilities: z.object({
    answerEntryAllowed: z.literal(false),
    fileUploadAllowed: z.literal(false),
    saveAllowed: z.literal(false),
    submitAllowed: z.literal(false),
    bookingAllowed: z.literal(false),
    crmWriteAllowed: z.literal(false),
  }).strict(),
  safety: z.object({
    providerMode: z.literal("disabled"),
    providerCallsAllowed: z.literal(false),
    emailSent: z.literal(false),
    executionAllowed: z.literal(false),
    claimable: z.literal(false),
    queueState: z.literal("not_queued"),
    deliveryStatus: z.literal("not_sent"),
  }).strict(),
  localInteraction: localInteractionSchema,
}).strict().superRefine((preview, context) => {
  let questions = 0;
  let options = 0;
  let localTextInteractionAvailable = false;
  preview.questionnaire.sections.forEach((section) => {
    questions += section.questions.length;
    section.questions.forEach((question) => {
      options += question.options.length;
      if (
        question.responseType === "text"
        && question.conditionalRequired === false
        && question.conditionalVisibility === false
      ) {
        localTextInteractionAvailable = true;
      }
    });
  });
  if (preview.localInteraction.allowed !== localTextInteractionAvailable) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["localInteraction", "allowed"],
      message: "Local interaction availability does not match the immutable questionnaire",
    });
  }
  if (questions > 500 || options > 5000) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["questionnaire", "sections"],
      message: "Questionnaire preview aggregate limit exceeded",
    });
  }
  if (
    utf8ByteLength(JSON.stringify(preview)) > MAX_QUESTIONNAIRE_PREVIEW_BYTES
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Questionnaire preview size limit exceeded",
    });
  }
});

const questionnaireQuestionV3Schema = z.object({
  questionRef: campaignQuestionRefSchema,
  sequence: z.literal(1),
  label: canonicalDisplayText(500),
  helpText: nullableDisplayText(2000),
  responseType: z.literal("text"),
  required: z.literal(false),
  conditionalRequired: z.literal(false),
  conditionalVisibility: z.literal(false),
  options: z.tuple([]),
}).strict();

const questionnaireSectionV3Schema = z.object({
  sequence: z.literal(1),
  title: canonicalDisplayText(300),
  description: nullableDisplayText(2000),
  questions: z.tuple([questionnaireQuestionV3Schema]),
}).strict();

const persistedQuestionnairePreviewShape = {
  source: z.literal("immutable_blueprint_run_snapshot"),
  questionnaire: z.object({
    name: canonicalDisplayText(300),
    version: z.number().int().positive().safe(),
    sections: z.tuple([questionnaireSectionV3Schema]),
  }).strict(),
  capabilities: z.object({
    draftReadAllowed: z.literal(true),
    draftAnswerEntryAllowed: z.literal(true),
    draftSaveAllowed: z.literal(true),
    draftClearAllowed: z.literal(true),
    fileUploadAllowed: z.literal(false),
    submitAllowed: z.literal(false),
    bookingAllowed: z.literal(false),
    crmWriteAllowed: z.literal(false),
  }).strict(),
  draftPersistence: z.object({
    schemaVersion: z.literal(
      "campaign_uat_public_questionnaire_draft_persistence.v1",
    ),
    mode: z.literal("same_verified_session_encrypted_text"),
    allowed: z.literal(true),
    sessionBound: z.literal(true),
    supportedResponseTypes: z.tuple([z.literal("text")]),
    maxAnswers: z.literal(1),
    maxCharacters: z.literal(200),
    maxUtf8Bytes: z.literal(800),
    clearAllowed: z.literal(true),
    browserStorageAllowed: z.literal(false),
    telemetryAllowed: z.literal(false),
    submissionAllowed: z.literal(false),
  }).strict(),
  safety: z.object({
    providerMode: z.literal("disabled"),
    providerCallsAllowed: z.literal(false),
    emailSent: z.literal(false),
    executionAllowed: z.literal(false),
    claimable: z.literal(false),
    queueState: z.literal("not_queued"),
    deliveryStatus: z.literal("not_sent"),
  }).strict(),
};

const validatePersistedQuestionnairePreviewSize = (preview, context) => {
  if (utf8ByteLength(JSON.stringify(preview)) > MAX_QUESTIONNAIRE_PREVIEW_BYTES) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["questionnaire"],
      message: "Questionnaire preview size limit exceeded",
    });
  }
};

const publicQuestionnairePreviewV3Schema = z.object({
  schemaVersion: z.literal("campaign_uat_public_questionnaire_preview.v3"),
  ...persistedQuestionnairePreviewShape,
}).strict().superRefine(validatePersistedQuestionnairePreviewSize);

const responseRehearsalSchema = z.object({
  schemaVersion: z.literal(
    "campaign_uat_public_questionnaire_response_rehearsal.v1",
  ),
  mode: z.literal("single_testerson_text_response"),
  allowed: z.literal(true),
  explicitConfirmationRequired: z.literal(true),
  rehearsalSubmitAllowed: z.literal(true),
  legacySubmissionWriteAllowed: z.literal(false),
  engagementWriteAllowed: z.literal(false),
}).strict();

const publicQuestionnairePreviewV4Schema = z.object({
  schemaVersion: z.literal("campaign_uat_public_questionnaire_preview.v4"),
  ...persistedQuestionnairePreviewShape,
  responseRehearsal: responseRehearsalSchema,
}).strict().superRefine(validatePersistedQuestionnairePreviewSize);

const multiAnswerOptionSchema = z.object({
  sequence: z.number().int().positive().safe(),
  optionRef: campaignOptionRefSchema,
  label: canonicalDisplayText(500),
}).strict();

const multiAnswerQuestionCommonShape = {
  questionRef: campaignQuestionRefV2Schema,
  sequence: z.number().int().positive().safe(),
  label: canonicalDisplayText(500),
  helpText: nullableDisplayText(2000),
  required: z.boolean(),
  conditionalRequired: z.literal(false),
  conditionalVisibility: z.literal(false),
};

const multiAnswerQuestionSchema = z.discriminatedUnion("responseType", [
  z.object({
    ...multiAnswerQuestionCommonShape,
    responseType: z.literal("text"),
    options: z.tuple([]),
    answerConstraints: z.object({
      valueType: z.literal("text"),
      maxCharacters: z.literal(200),
      maxUtf8Bytes: z.literal(800),
      multiline: z.literal(false),
    }).strict(),
  }).strict(),
  z.object({
    ...multiAnswerQuestionCommonShape,
    responseType: z.literal("textarea"),
    options: z.tuple([]),
    answerConstraints: z.object({
      valueType: z.literal("text"),
      maxCharacters: z.literal(2000),
      maxUtf8Bytes: z.literal(8000),
      multiline: z.literal(true),
    }).strict(),
  }).strict(),
  z.object({
    ...multiAnswerQuestionCommonShape,
    responseType: z.literal("date"),
    options: z.tuple([]),
    answerConstraints: z.object({
      valueType: z.literal("date"),
      minDate: z.literal("1900-01-01"),
      maxDate: z.literal("2100-12-31"),
    }).strict(),
  }).strict(),
  ...["dropdown", "radio"].map((responseType) => z.object({
    ...multiAnswerQuestionCommonShape,
    responseType: z.literal(responseType),
    options: sequencedArray(multiAnswerOptionSchema, 20).min(2),
    answerConstraints: z.object({
      valueType: z.literal("single_choice"),
    }).strict(),
  }).strict()),
  z.object({
    ...multiAnswerQuestionCommonShape,
    responseType: z.literal("checkbox"),
    options: z.tuple([]),
    answerConstraints: z.object({
      valueType: z.literal("boolean"),
      requiredTrue: z.boolean(),
    }).strict(),
  }).strict().superRefine((question, context) => {
    if (question.answerConstraints.requiredTrue !== question.required) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["answerConstraints", "requiredTrue"],
        message: "Checkbox required constraint differs from the question",
      });
    }
  }),
]);

const multiAnswerSectionSchema = z.object({
  sequence: z.number().int().positive().safe(),
  title: canonicalDisplayText(300),
  description: nullableDisplayText(2000),
  questions: sequencedArray(multiAnswerQuestionSchema, 8).min(1),
}).strict();

const multiAnswerDraftPersistenceSchema = z.object({
  schemaVersion: z.literal(
    "campaign_uat_public_questionnaire_draft_persistence.v2",
  ),
  mode: z.literal("same_verified_session_encrypted_multi_answer"),
  allowed: z.literal(true),
  sessionBound: z.literal(true),
  supportedResponseTypes: z.tuple([
    z.literal("text"),
    z.literal("textarea"),
    z.literal("date"),
    z.literal("dropdown"),
    z.literal("radio"),
    z.literal("checkbox"),
  ]),
  maxSections: z.literal(2),
  maxAnswers: z.literal(8),
  maxCanonicalPayloadUtf8Bytes: z.literal(MAX_MULTI_ANSWER_CANONICAL_BYTES),
  clearAllowed: z.literal(true),
  browserStorageAllowed: z.literal(false),
  telemetryAllowed: z.literal(false),
  submissionAllowed: z.literal(false),
}).strict();

const multiAnswerResponseRehearsalSchema = z.object({
  schemaVersion: z.literal(
    "campaign_uat_public_questionnaire_response_rehearsal.v2",
  ),
  mode: z.literal("testerson_multi_answer_response"),
  allowed: z.literal(true),
  explicitConfirmationRequired: z.literal(true),
  rehearsalSubmitAllowed: z.literal(true),
  legacySubmissionWriteAllowed: z.literal(false),
  engagementWriteAllowed: z.literal(false),
}).strict();

const publicQuestionnairePreviewV5Schema = z.object({
  schemaVersion: z.literal("campaign_uat_public_questionnaire_preview.v5"),
  source: z.literal("immutable_blueprint_run_snapshot"),
  questionnaire: z.object({
    name: canonicalDisplayText(300),
    version: z.number().int().positive().safe(),
    answerSetRef: campaignAnswerSetRefSchema,
    sections: sequencedArray(multiAnswerSectionSchema, 2).min(1),
  }).strict(),
  capabilities: persistedQuestionnairePreviewShape.capabilities,
  draftPersistence: multiAnswerDraftPersistenceSchema,
  responseRehearsal: multiAnswerResponseRehearsalSchema,
  safety: persistedQuestionnairePreviewShape.safety,
}).strict().superRefine((preview, context) => {
  const questions = preview.questionnaire.sections.flatMap(
    (section) => section.questions,
  );
  const questionRefs = questions.map((question) => question.questionRef);
  const options = questions.flatMap((question) => question.options);
  const optionRefs = options.map((option) => option.optionRef);
  const firstQuestion = questions[0];
  if (questions.length < 2 || questions.length > 8) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["questionnaire", "sections"],
      message: "Multi-answer questionnaire question count is invalid",
    });
  }
  if (
    !firstQuestion
    || firstQuestion.responseType !== "text"
    || firstQuestion.required !== true
    || firstQuestion.options.length !== 0
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["questionnaire", "sections", 0, "questions", 0],
      message: "Multi-answer first-question anchor is invalid",
    });
  }
  if (new Set(questionRefs).size !== questionRefs.length) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["questionnaire", "sections"],
      message: "Duplicate question reference",
    });
  }
  if (options.length > 50 || new Set(optionRefs).size !== optionRefs.length) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["questionnaire", "sections"],
      message: "Multi-answer option set is invalid",
    });
  }
  validatePersistedQuestionnairePreviewSize(preview, context);
});

const campaignMultiAnswerTextSchema = campaignDraftTextSchema;
const campaignMultiAnswerTextareaSchema = z.string()
  .refine((value) => !containsUnpairedSurrogate(value))
  .refine((value) => value.normalize("NFC") === value)
  .refine((value) => value === value.trim())
  .refine((value) => !value.includes("\r"))
  .refine((value) => {
    for (const character of value) {
      const codePoint = character.codePointAt(0);
      if (
        ((codePoint >= 0 && codePoint <= 0x1f) && codePoint !== 0x0a)
        || (codePoint >= 0x7f && codePoint <= 0x9f)
        || codePoint === 0x061c
        || codePoint === 0x200e
        || codePoint === 0x200f
        || codePoint === 0x2028
        || codePoint === 0x2029
        || (codePoint >= 0x202a && codePoint <= 0x202e)
        || (codePoint >= 0x2066 && codePoint <= 0x2069)
      ) return false;
    }
    return true;
  })
  .refine((value) => Array.from(value).length >= 1)
  .refine((value) => Array.from(value).length <= 2000)
  .refine((value) => utf8ByteLength(value) <= 8000);

const campaignMultiAnswerDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/)
  .refine((value) => {
    const [year, month, day] = value.split("-").map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.getUTCFullYear() === year
      && date.getUTCMonth() === month - 1
      && date.getUTCDate() === day
      && value >= "1900-01-01"
      && value <= "2100-12-31";
  });

const multiAnswerTypedAnswerSchema = z.discriminatedUnion("responseType", [
  z.object({
    questionRef: campaignQuestionRefV2Schema,
    responseType: z.literal("text"),
    value: campaignMultiAnswerTextSchema,
  }).strict(),
  z.object({
    questionRef: campaignQuestionRefV2Schema,
    responseType: z.literal("textarea"),
    value: campaignMultiAnswerTextareaSchema,
  }).strict(),
  z.object({
    questionRef: campaignQuestionRefV2Schema,
    responseType: z.literal("date"),
    value: campaignMultiAnswerDateSchema,
  }).strict(),
  z.object({
    questionRef: campaignQuestionRefV2Schema,
    responseType: z.literal("dropdown"),
    value: campaignOptionRefSchema,
  }).strict(),
  z.object({
    questionRef: campaignQuestionRefV2Schema,
    responseType: z.literal("radio"),
    value: campaignOptionRefSchema,
  }).strict(),
  z.object({
    questionRef: campaignQuestionRefV2Schema,
    responseType: z.literal("checkbox"),
    value: z.boolean(),
  }).strict(),
]);

const draftAnswerSchema = z.object({
  questionRef: campaignQuestionRefSchema,
  value: campaignDraftTextSchema,
}).strict();

const questionnaireDraftStateSchema = z.object({
  schemaVersion: z.literal("campaign_uat_questionnaire_draft_state.v1"),
  state: z.enum(["empty", "saved", "cleared"]),
  revision: z.number().int().nonnegative().safe(),
  changedAt: z.string().datetime({ offset: true }).nullable(),
  answers: z.array(draftAnswerSchema).max(1),
}).strict().superRefine((current, context) => {
  const emptyIsExact = current.state === "empty"
    && current.revision === 0
    && current.changedAt === null
    && current.answers.length === 0;
  const savedIsExact = current.state === "saved"
    && current.revision > 0
    && current.changedAt !== null
    && current.answers.length === 1;
  const clearedIsExact = current.state === "cleared"
    && current.revision >= 2
    && current.changedAt !== null
    && current.answers.length === 0;
  if (!emptyIsExact && !savedIsExact && !clearedIsExact) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Draft state is inconsistent",
    });
  }
});

const draftAccessSchema = z.object({
  schemaVersion: z.literal("campaign_uat_questionnaire_draft_access.v1"),
  mode: z.literal("same_verified_session"),
  requestHeader: z.literal("X-Campaign-Draft-Token"),
  requestToken: campaignDraftRequestTokenSchema,
  expiresAt: z.string().datetime({ offset: true }),
  current: questionnaireDraftStateSchema,
}).strict();

const responseReceiptShape = {
  schemaVersion: z.literal("campaign_uat_questionnaire_response_receipt.v1"),
  state: z.enum(["submitted", "reviewed"]),
  responseRevision: z.number().int().positive().safe(),
  answerCount: z.literal(1),
  submittedAt: z.string().datetime({ offset: true }),
  reviewedAt: z.string().datetime({ offset: true }).nullable(),
};

const validateResponseReceipt = (receipt, context) => {
  const submittedAt = new Date(receipt.submittedAt).getTime();
  const reviewedAt = receipt.reviewedAt === null
    ? null
    : new Date(receipt.reviewedAt).getTime();
  if (
    (receipt.state === "submitted"
      && (receipt.responseRevision !== 1 || receipt.reviewedAt !== null))
    || (receipt.state === "reviewed"
      && (receipt.responseRevision !== 2
        || reviewedAt === null
        || reviewedAt < submittedAt))
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Response receipt lifecycle is inconsistent",
    });
  }
};

const responseReceiptSchema = z.object(responseReceiptShape)
  .strict()
  .superRefine(validateResponseReceipt);

const responseAccessSchema = z.union([
  z.object({
    schemaVersion: z.literal("campaign_uat_questionnaire_response_access.v1"),
    mode: z.literal("same_verified_session"),
    state: z.literal("draft"),
    expiresAt: z.string().datetime({ offset: true }),
    requestHeader: z.literal("X-Campaign-Draft-Token"),
    requestToken: campaignDraftRequestTokenSchema,
    currentDraft: questionnaireDraftStateSchema,
    receipt: z.null(),
  }).strict(),
  z.object({
    schemaVersion: z.literal("campaign_uat_questionnaire_response_access.v1"),
    mode: z.literal("same_verified_session"),
    state: z.enum(["submitted", "reviewed"]),
    expiresAt: z.string().datetime({ offset: true }),
    currentDraft: z.null(),
    receipt: responseReceiptSchema,
  }).strict(),
]).superRefine((access, context) => {
  if (access.state !== "draft" && access.receipt.state !== access.state) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["receipt", "state"],
      message: "Response receipt state differs from session state",
    });
  }
});

const questionnaireDraftStateV2Schema = z.object({
  schemaVersion: z.literal("campaign_uat_questionnaire_draft_state.v2"),
  state: z.enum(["empty", "saved", "cleared"]),
  revision: z.number().int().nonnegative().safe(),
  changedAt: z.string().datetime({ offset: true }).nullable(),
  answerSetRef: campaignAnswerSetRefSchema,
  answers: z.array(multiAnswerTypedAnswerSchema).max(8),
}).strict().superRefine((current, context) => {
  const emptyIsExact = current.state === "empty"
    && current.revision === 0
    && current.changedAt === null
    && current.answers.length === 0;
  const savedIsExact = current.state === "saved"
    && current.revision > 0
    && current.changedAt !== null
    && current.answers.length >= 1
    && current.answers.length <= 8;
  const clearedIsExact = current.state === "cleared"
    && current.revision >= 2
    && current.changedAt !== null
    && current.answers.length === 0;
  if (!emptyIsExact && !savedIsExact && !clearedIsExact) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Multi-answer draft state is inconsistent",
    });
  }
  const refs = current.answers.map((answer) => answer.questionRef);
  if (new Set(refs).size !== refs.length) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["answers"],
      message: "Duplicate draft answer reference",
    });
  }
  if (utf8ByteLength(JSON.stringify(current.answers)) > MAX_MULTI_ANSWER_CANONICAL_BYTES) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["answers"],
      message: "Multi-answer draft payload exceeds its canonical limit",
    });
  }
});

const responseReceiptV2Shape = {
  schemaVersion: z.literal("campaign_uat_questionnaire_response_receipt.v2"),
  state: z.enum(["submitted", "reviewed"]),
  responseRevision: z.number().int().positive().safe(),
  answerCount: z.number().int().min(1).max(8).safe(),
  answerSetRef: campaignAnswerSetRefSchema,
  submittedAt: z.string().datetime({ offset: true }),
  reviewedAt: z.string().datetime({ offset: true }).nullable(),
};

const responseReceiptV2Schema = z.object(responseReceiptV2Shape)
  .strict()
  .superRefine(validateResponseReceipt);

const responseAccessV2Schema = z.union([
  z.object({
    schemaVersion: z.literal("campaign_uat_questionnaire_response_access.v2"),
    mode: z.literal("same_verified_session"),
    state: z.literal("draft"),
    answerSetRef: campaignAnswerSetRefSchema,
    expiresAt: z.string().datetime({ offset: true }),
    requestHeader: z.literal("X-Campaign-Draft-Token"),
    requestToken: campaignDraftRequestTokenV2Schema,
    currentDraft: questionnaireDraftStateV2Schema,
    receipt: z.null(),
  }).strict(),
  z.object({
    schemaVersion: z.literal("campaign_uat_questionnaire_response_access.v2"),
    mode: z.literal("same_verified_session"),
    state: z.enum(["submitted", "reviewed"]),
    answerSetRef: campaignAnswerSetRefSchema,
    expiresAt: z.string().datetime({ offset: true }),
    currentDraft: z.null(),
    receipt: responseReceiptV2Schema,
  }).strict(),
]).superRefine((access, context) => {
  const child = access.state === "draft" ? access.currentDraft : access.receipt;
  if (child.answerSetRef !== access.answerSetRef) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: [access.state === "draft" ? "currentDraft" : "receipt", "answerSetRef"],
      message: "Response access answer-set binding differs",
    });
  }
  if (access.state !== "draft" && access.receipt.state !== access.state) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["receipt", "state"],
      message: "Response receipt state differs from session state",
    });
  }
});

const bootstrapResponseSchema = z
  .object({
    ok: z.literal(true),
    data: z
      .object({
        campaignName: z
          .string()
          .min(1)
          .max(200)
          .refine((value) => value === value.trim()),
        maskedDestination: z.string().regex(MASKED_DESTINATION),
        expiresAt: z.string().datetime({ offset: true }),
        verificationRequired: z.literal(true),
        verificationMethod: z.literal("email_otp"),
        uatOtpRehearsalAvailable: z.boolean(),
      })
      .strict()
      .superRefine((value, context) => {
        if (
          value.uatOtpRehearsalAvailable
          && !MASKED_TEST_DESTINATION.test(value.maskedDestination)
        ) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["maskedDestination"],
            message: "Reserved test destination required",
          });
        }
      }),
  })
  .strict();

const PUBLIC_ERROR_BY_CODE = Object.freeze({
  CAMPAIGN_ACCESS_INVALID: INVALID_PUBLIC_ERROR,
  CAMPAIGN_ACCESS_REQUEST_REJECTED: INVALID_PUBLIC_ERROR,
  CAMPAIGN_ACCESS_RATE_LIMITED: RATE_LIMIT_PUBLIC_ERROR,
  CAMPAIGN_ACCESS_UNAVAILABLE: DEFAULT_PUBLIC_ERROR,
  CAMPAIGN_ACCESS_CODE_INVALID: CODE_INVALID_PUBLIC_ERROR,
  CAMPAIGN_ACCESS_CHALLENGE_EXPIRED: CHALLENGE_EXPIRED_PUBLIC_ERROR,
  CAMPAIGN_ACCESS_CHALLENGE_LOCKED: CHALLENGE_LOCKED_PUBLIC_ERROR,
  CAMPAIGN_2FA_DELIVERY_DISABLED: DELIVERY_DISABLED_PUBLIC_ERROR,
  CAMPAIGN_UAT_DRAFT_REVISION_CONFLICT: DRAFT_CONFLICT_PUBLIC_ERROR,
  CAMPAIGN_UAT_DRAFT_IDEMPOTENCY_CONFLICT: DRAFT_CONFLICT_PUBLIC_ERROR,
  CAMPAIGN_UAT_DRAFT_CLEAR_INVALID: DRAFT_CLEAR_PUBLIC_ERROR,
  CAMPAIGN_UAT_DRAFT_VALIDATION_FAILED: DRAFT_VALIDATION_PUBLIC_ERROR,
  CAMPAIGN_UAT_PERSISTED_DRAFT_UNAVAILABLE: DEFAULT_PUBLIC_ERROR,
  CAMPAIGN_UAT_RESPONSE_REVISION_CONFLICT: RESPONSE_CONFLICT_PUBLIC_ERROR,
  CAMPAIGN_UAT_RESPONSE_IDEMPOTENCY_CONFLICT: RESPONSE_CONFLICT_PUBLIC_ERROR,
  CAMPAIGN_UAT_RESPONSE_ALREADY_SUBMITTED: RESPONSE_CONFLICT_PUBLIC_ERROR,
  CAMPAIGN_UAT_RESPONSE_VALIDATION_FAILED: RESPONSE_VALIDATION_PUBLIC_ERROR,
  CAMPAIGN_UAT_RESPONSE_REHEARSAL_UNAVAILABLE: DEFAULT_PUBLIC_ERROR,
});

const TERMINAL_PUBLIC_ERROR_CODES = new Set([
  "CAMPAIGN_ACCESS_INVALID",
  "CAMPAIGN_ACCESS_CHALLENGE_EXPIRED",
  "CAMPAIGN_ACCESS_CHALLENGE_LOCKED",
  "CAMPAIGN_2FA_DELIVERY_DISABLED",
  "CAMPAIGN_UAT_PERSISTED_DRAFT_UNAVAILABLE",
  "CAMPAIGN_UAT_RESPONSE_REHEARSAL_UNAVAILABLE",
]);

const DRAFT_CONFLICT_PUBLIC_ERROR_CODES = new Set([
  "CAMPAIGN_UAT_DRAFT_REVISION_CONFLICT",
  "CAMPAIGN_UAT_DRAFT_IDEMPOTENCY_CONFLICT",
  "CAMPAIGN_UAT_DRAFT_CLEAR_INVALID",
]);

const RESPONSE_CONFLICT_PUBLIC_ERROR_CODES = new Set([
  "CAMPAIGN_UAT_RESPONSE_REVISION_CONFLICT",
  "CAMPAIGN_UAT_RESPONSE_IDEMPOTENCY_CONFLICT",
  "CAMPAIGN_UAT_RESPONSE_ALREADY_SUBMITTED",
]);

const challengeResponseSchema = z.object({
  ok: z.literal(true),
  data: z.object({
    challengeId: z.string().regex(UUID_V4_PATTERN),
    maskedDestination: z.string().regex(MASKED_TEST_DESTINATION),
    expiresAt: z.string().datetime({ offset: true }),
    delivery: z.object({
      providerMode: z.literal("disabled"),
      status: z.literal("captured_for_uat"),
    }).strict(),
  }).strict(),
}).strict();

const verifyResponseSchema = z.object({
  ok: z.literal(true),
  data: z.object({
    verified: z.literal(true),
    continuationAllowed: z.literal(false),
    expiresAt: z.string().datetime({ offset: true }),
    csrfToken: csrfTokenSchema,
  }).strict(),
}).strict();

const sessionV2DataSchema = z.object({
  verified: z.literal(true),
  continuationAllowed: z.literal(false),
  expiresAt: z.string().datetime({ offset: true }),
  questionnairePreview: publicQuestionnairePreviewV2Schema,
}).strict();

const sessionV3DataSchema = z.object({
  verified: z.literal(true),
  continuationAllowed: z.literal(false),
  expiresAt: z.string().datetime({ offset: true }),
  questionnairePreview: publicQuestionnairePreviewV3Schema,
  draftAccess: draftAccessSchema,
}).strict().superRefine((session, context) => {
  const questionRef = session.questionnairePreview.questionnaire
    .sections[0].questions[0].questionRef;
  if (session.draftAccess.expiresAt !== session.expiresAt) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["draftAccess", "expiresAt"],
      message: "Draft and session expiry differ",
    });
  }
  if (
    session.draftAccess.current.changedAt !== null
    && new Date(session.draftAccess.current.changedAt).getTime()
      >= new Date(session.expiresAt).getTime()
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["draftAccess", "current", "changedAt"],
      message: "Draft change time exceeds the session expiry",
    });
  }
  if (
    session.draftAccess.current.answers.some(
      (answer) => answer.questionRef !== questionRef,
    )
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["draftAccess", "current", "answers"],
      message: "Draft answer identity differs from the immutable questionnaire",
    });
  }
});

const sessionV4DataSchema = z.object({
  verified: z.literal(true),
  continuationAllowed: z.literal(false),
  expiresAt: z.string().datetime({ offset: true }),
  questionnairePreview: publicQuestionnairePreviewV4Schema,
  responseAccess: responseAccessSchema,
}).strict().superRefine((session, context) => {
  const questionRef = session.questionnairePreview.questionnaire
    .sections[0].questions[0].questionRef;
  if (session.responseAccess.expiresAt !== session.expiresAt) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["responseAccess", "expiresAt"],
      message: "Response and session expiry differ",
    });
  }
  if (session.responseAccess.state === "draft") {
    const draft = session.responseAccess.currentDraft;
    if (
      (draft.changedAt !== null
        && new Date(draft.changedAt).getTime() >= new Date(session.expiresAt).getTime())
      || draft.answers.some((answer) => answer.questionRef !== questionRef)
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["responseAccess", "currentDraft"],
        message: "Draft evidence differs from the response session",
      });
    }
    return;
  }
  const receipt = session.responseAccess.receipt;
  if (
    new Date(receipt.submittedAt).getTime() >= new Date(session.expiresAt).getTime()
    || (receipt.reviewedAt !== null
      && new Date(receipt.reviewedAt).getTime() >= new Date(session.expiresAt).getTime())
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["responseAccess", "receipt"],
      message: "Response receipt exceeds the session expiry",
    });
  }
});

const sessionV5DataSchema = z.object({
  verified: z.literal(true),
  continuationAllowed: z.literal(false),
  expiresAt: z.string().datetime({ offset: true }),
  questionnairePreview: publicQuestionnairePreviewV5Schema,
  responseAccess: responseAccessV2Schema,
}).strict().superRefine((session, context) => {
  const preview = session.questionnairePreview;
  const access = session.responseAccess;
  const answerSetRef = preview.questionnaire.answerSetRef;
  if (access.expiresAt !== session.expiresAt) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["responseAccess", "expiresAt"],
      message: "Response and session expiry differ",
    });
  }
  if (access.answerSetRef !== answerSetRef) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["responseAccess", "answerSetRef"],
      message: "Response access differs from the immutable answer set",
    });
  }
  if (access.state === "draft") {
    const draft = access.currentDraft;
    if (
      draft.changedAt !== null
      && new Date(draft.changedAt).getTime() >= new Date(session.expiresAt).getTime()
    ) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["responseAccess", "currentDraft", "changedAt"],
        message: "Draft change time exceeds the session expiry",
      });
    }
    const orderedQuestions = preview.questionnaire.sections.flatMap(
      (section) => section.questions,
    );
    let priorIndex = -1;
    draft.answers.forEach((answer, answerIndex) => {
      const questionIndex = orderedQuestions.findIndex(
        (question) => question.questionRef === answer.questionRef,
      );
      const question = orderedQuestions[questionIndex];
      const choiceRefValid = !question
        || !["dropdown", "radio"].includes(answer.responseType)
        || question.options.some((option) => option.optionRef === answer.value);
      if (
        questionIndex <= priorIndex
        || !question
        || question.responseType !== answer.responseType
        || !choiceRefValid
      ) {
        context.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["responseAccess", "currentDraft", "answers", answerIndex],
          message: "Draft answer differs from the immutable ordered questionnaire",
        });
      }
      priorIndex = questionIndex;
    });
    return;
  }
  const receipt = access.receipt;
  if (
    new Date(receipt.submittedAt).getTime() >= new Date(session.expiresAt).getTime()
    || (receipt.reviewedAt !== null
      && new Date(receipt.reviewedAt).getTime() >= new Date(session.expiresAt).getTime())
  ) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["responseAccess", "receipt"],
      message: "Response receipt exceeds the session expiry",
    });
  }
});

const sessionResponseSchema = z.object({
  ok: z.literal(true),
  data: z.union([
    sessionV2DataSchema,
    sessionV3DataSchema,
    sessionV4DataSchema,
    sessionV5DataSchema,
  ]),
}).strict();

const resumeSessionResponseSchema = z.object({
  ok: z.literal(true),
  data: z.union([sessionV3DataSchema, sessionV4DataSchema, sessionV5DataSchema]),
}).strict();

const draftSaveMutationSchema = z.object({
  schemaVersion: z.literal("campaign_uat_questionnaire_draft_mutation.v1"),
  action: z.literal("save"),
  idempotencyKey: z.string().regex(LOWERCASE_UUID_V4_PATTERN),
  expectedRevision: z.number().int().nonnegative().safe(),
  answers: z.tuple([draftAnswerSchema]),
}).strict();

const draftClearMutationSchema = z.object({
  schemaVersion: z.literal("campaign_uat_questionnaire_draft_mutation.v1"),
  action: z.literal("clear"),
  idempotencyKey: z.string().regex(LOWERCASE_UUID_V4_PATTERN),
  expectedRevision: z.number().int().positive().safe(),
  confirmation: z.literal("clear_testerson_2_saved_draft"),
}).strict();

const draftMutationSchema = z.discriminatedUnion("action", [
  draftSaveMutationSchema,
  draftClearMutationSchema,
]);

const draftSaveMutationV2Schema = z.object({
  schemaVersion: z.literal("campaign_uat_questionnaire_draft_mutation.v2"),
  action: z.literal("save"),
  answerSetRef: campaignAnswerSetRefSchema,
  idempotencyKey: z.string().regex(LOWERCASE_UUID_V4_PATTERN),
  expectedRevision: z.number().int().nonnegative().safe(),
  answers: z.array(multiAnswerTypedAnswerSchema).min(1).max(8),
}).strict().superRefine((mutation, context) => {
  const refs = mutation.answers.map((answer) => answer.questionRef);
  if (new Set(refs).size !== refs.length) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["answers"],
      message: "Duplicate multi-answer mutation reference",
    });
  }
  if (utf8ByteLength(JSON.stringify(mutation.answers)) > MAX_MULTI_ANSWER_CANONICAL_BYTES) {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["answers"],
      message: "Multi-answer mutation payload exceeds its canonical limit",
    });
  }
});

const draftClearMutationV2Schema = z.object({
  schemaVersion: z.literal("campaign_uat_questionnaire_draft_mutation.v2"),
  action: z.literal("clear"),
  answerSetRef: campaignAnswerSetRefSchema,
  idempotencyKey: z.string().regex(LOWERCASE_UUID_V4_PATTERN),
  expectedRevision: z.number().int().positive().safe(),
  confirmation: z.literal("clear_testerson_2_multi_answer_saved_draft"),
}).strict();

const draftMutationV2Schema = z.discriminatedUnion("action", [
  draftSaveMutationV2Schema,
  draftClearMutationV2Schema,
]);

const anyDraftMutationSchema = z.union([draftMutationSchema, draftMutationV2Schema]);

const draftMutationResultSchema = z.object({
  ok: z.literal(true),
  data: z.object({
    schemaVersion: z.literal(
      "campaign_uat_questionnaire_draft_mutation_result.v1",
    ),
    state: z.enum(["saved", "cleared"]),
    revision: z.number().int().positive().safe(),
    changedAt: z.string().datetime({ offset: true }),
    expiresAt: z.string().datetime({ offset: true }),
    idempotentReplay: z.boolean(),
  }).strict(),
}).strict();

const draftMutationResultV2Schema = z.object({
  ok: z.literal(true),
  data: z.object({
    schemaVersion: z.literal(
      "campaign_uat_questionnaire_draft_mutation_result.v2",
    ),
    state: z.enum(["saved", "cleared"]),
    revision: z.number().int().positive().safe(),
    changedAt: z.string().datetime({ offset: true }),
    expiresAt: z.string().datetime({ offset: true }),
    answerSetRef: campaignAnswerSetRefSchema,
    answerCount: z.number().int().min(0).max(8).safe(),
    idempotentReplay: z.boolean(),
  }).strict().superRefine((result, context) => {
    if (
      (result.state === "saved" && result.answerCount < 1)
      || (result.state === "cleared" && result.answerCount !== 0)
    ) context.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["answerCount"],
      message: "Multi-answer mutation result count is invalid",
    });
  }),
}).strict();

const anyDraftMutationResultSchema = z.union([
  draftMutationResultSchema,
  draftMutationResultV2Schema,
]);

const responseSubmitMutationSchema = z.object({
  schemaVersion: z.literal("campaign_uat_questionnaire_response_submit.v1"),
  action: z.literal("submit"),
  confirmation: z.literal("SUBMIT_TESTERSON_2_RESPONSE_REHEARSAL"),
  expectedDraftRevision: z.number().int().positive().safe(),
  idempotencyKey: z.string().regex(LOWERCASE_UUID_V4_PATTERN),
}).strict();

const responseSubmitMutationV2Schema = z.object({
  schemaVersion: z.literal("campaign_uat_questionnaire_response_submit.v2"),
  action: z.literal("submit"),
  confirmation: z.literal(
    "SUBMIT_TESTERSON_2_MULTI_ANSWER_RESPONSE_REHEARSAL",
  ),
  answerSetRef: campaignAnswerSetRefSchema,
  expectedDraftRevision: z.number().int().positive().safe(),
  idempotencyKey: z.string().regex(LOWERCASE_UUID_V4_PATTERN),
}).strict();

const anyResponseSubmitMutationSchema = z.union([
  responseSubmitMutationSchema,
  responseSubmitMutationV2Schema,
]);

const responseSubmitResultSchema = z.object({
  ok: z.literal(true),
  data: z.object({
    schemaVersion: z.literal("campaign_uat_questionnaire_response_receipt.v1"),
    state: z.literal("submitted"),
    responseRevision: z.literal(1),
    answerCount: z.literal(1),
    submittedAt: z.string().datetime({ offset: true }),
    reviewedAt: z.null(),
    idempotentReplay: z.boolean(),
  }).strict(),
}).strict();

const responseSubmitResultV2Schema = z.object({
  ok: z.literal(true),
  data: z.object({
    schemaVersion: z.literal("campaign_uat_questionnaire_response_receipt.v2"),
    state: z.literal("submitted"),
    responseRevision: z.literal(1),
    answerCount: z.number().int().min(1).max(8).safe(),
    answerSetRef: campaignAnswerSetRefSchema,
    submittedAt: z.string().datetime({ offset: true }),
    reviewedAt: z.null(),
    idempotentReplay: z.boolean(),
  }).strict(),
}).strict();

const anyResponseSubmitResultSchema = z.union([
  responseSubmitResultSchema,
  responseSubmitResultV2Schema,
]);

const trimTrailingSlash = (value) => value.replace(/\/+$/, "");

export const resolveCampaignAccessApiBaseUrl = () => {
  const configuredBase = (process.env.REACT_APP_API_BASE_URL || "").trim();
  if (configuredBase) {
    if (configuredBase.startsWith("/")) {
      return trimTrailingSlash(configuredBase);
    }

    try {
      const configuredUrl = new URL(configuredBase);
      if (
        typeof window !== "undefined" &&
        ["localhost", "127.0.0.1"].includes(configuredUrl.hostname) &&
        !["localhost", "127.0.0.1"].includes(window.location.hostname)
      ) {
        configuredUrl.hostname = window.location.hostname;
      }
      return trimTrailingSlash(configuredUrl.toString());
    } catch (_error) {
      return trimTrailingSlash(configuredBase);
    }
  }

  if (typeof window === "undefined") {
    return "http://localhost:3001/api/v1";
  }
  return `${window.location.protocol}//${window.location.hostname}:3001/api/v1`;
};

const publicMessageForResponse = (response, payload) => {
  const code =
    payload &&
    typeof payload === "object" &&
    payload.error &&
    typeof payload.error === "object" &&
    typeof payload.error.code === "string"
      ? payload.error.code
      : null;

  if (code && PUBLIC_ERROR_BY_CODE[code]) {
    return PUBLIC_ERROR_BY_CODE[code];
  }
  if (response.status === 429) {
    return RATE_LIMIT_PUBLIC_ERROR;
  }
  return DEFAULT_PUBLIC_ERROR;
};

export class CampaignAccessApiError extends Error {
  constructor(
    publicMessage = DEFAULT_PUBLIC_ERROR,
    code = null,
    { transportFailure = false, httpStatus = null } = {},
  ) {
    super("Campaign access request failed");
    this.name = "CampaignAccessApiError";
    this.publicMessage = publicMessage;
    this.code = Object.prototype.hasOwnProperty.call(PUBLIC_ERROR_BY_CODE, code)
      ? code
      : null;
    this.terminal = this.code ? TERMINAL_PUBLIC_ERROR_CODES.has(this.code) : false;
    this.transportFailure = transportFailure === true;
    this.httpStatus = Number.isInteger(httpStatus) ? httpStatus : null;
    this.draftConflict = this.code
      ? DRAFT_CONFLICT_PUBLIC_ERROR_CODES.has(this.code)
      : false;
    this.draftValidation = this.code === "CAMPAIGN_UAT_DRAFT_VALIDATION_FAILED";
    this.responseConflict = this.code
      ? RESPONSE_CONFLICT_PUBLIC_ERROR_CODES.has(this.code)
      : false;
    this.responseValidation = this.code
      === "CAMPAIGN_UAT_RESPONSE_VALIDATION_FAILED";
  }
}

const responseErrorCode = (payload) => {
  const code = payload?.error?.code;
  return typeof code === "string" && PUBLIC_ERROR_BY_CODE[code] ? code : null;
};

async function requestCampaignAccess(path, options, schema) {
  let response;
  try {
    response = await fetch(`${resolveCampaignAccessApiBaseUrl()}${path}`, {
      cache: "no-store",
      referrerPolicy: "no-referrer",
      redirect: "error",
      ...options,
    });
  } catch (_error) {
    throw new CampaignAccessApiError(DEFAULT_PUBLIC_ERROR, null, {
      transportFailure: true,
    });
  }

  let payload = null;
  try {
    payload = await response.json();
  } catch (_error) {
    // Malformed or non-JSON responses never reach the public UI.
  }
  if (!response.ok || response.status !== 200) {
    const code = responseErrorCode(payload);
    throw new CampaignAccessApiError(
      code ? PUBLIC_ERROR_BY_CODE[code] : publicMessageForResponse(response, payload),
      code,
      { httpStatus: response.status },
    );
  }
  const parsed = schema.safeParse(payload);
  if (!parsed.success) throw new CampaignAccessApiError();
  return parsed.data.data;
}

export const bootstrapCampaignAccess = async (token, { signal } = {}) => {
  if (typeof token !== "string" || !LINK_TOKEN_PATTERN.test(token)) {
    throw new CampaignAccessApiError(INVALID_PUBLIC_ERROR);
  }
  return requestCampaignAccess(
    "/campaign-access/bootstrap",
    {
      method: "POST",
      credentials: "omit",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
      ...(signal ? { signal } : {}),
    },
    bootstrapResponseSchema,
  );
};

export const requestCampaignAccessChallenge = async (token, { signal } = {}) => {
  if (typeof token !== "string" || !LINK_TOKEN_PATTERN.test(token)) {
    throw new CampaignAccessApiError(INVALID_PUBLIC_ERROR, "CAMPAIGN_ACCESS_INVALID");
  }
  return requestCampaignAccess(
    "/campaign-access/challenge",
    {
      method: "POST",
      credentials: "omit",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
      ...(signal ? { signal } : {}),
    },
    challengeResponseSchema,
  );
};

export const verifyCampaignAccess = async ({
  challengeId,
  otp,
  signal,
}) => {
  if (!UUID_V4_PATTERN.test(String(challengeId || "")) || !/^\d{6}$/.test(String(otp || ""))) {
    throw new CampaignAccessApiError(INVALID_PUBLIC_ERROR, "CAMPAIGN_ACCESS_INVALID");
  }
  return requestCampaignAccess(
    "/campaign-access/verify",
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ challengeId, otp }),
      ...(signal ? { signal } : {}),
    },
    verifyResponseSchema,
  );
};

export const probeCampaignAccessSession = async ({ csrfToken, signal }) => {
  if (!csrfTokenSchema.safeParse(csrfToken).success) {
    throw new CampaignAccessApiError();
  }
  return requestCampaignAccess(
    "/campaign-access/session",
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken,
      },
      body: "{}",
      ...(signal ? { signal } : {}),
    },
    sessionResponseSchema,
  );
};

export const resumeCampaignAccessSession = async ({ signal } = {}) =>
  requestCampaignAccess(
    "/campaign-access/session/resume",
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: "{}",
      ...(signal ? { signal } : {}),
    },
    resumeSessionResponseSchema,
  );

export const mutateCampaignQuestionnaireDraft = async ({
  requestToken,
  mutation,
  sessionExpiresAt,
  signal,
}) => {
  const parsedMutation = anyDraftMutationSchema.safeParse(mutation);
  if (!parsedMutation.success) throw new CampaignAccessApiError();
  const v2 = parsedMutation.data.schemaVersion
    === "campaign_uat_questionnaire_draft_mutation.v2";
  const requestTokenValid = v2
    ? campaignDraftRequestTokenV2Schema.safeParse(requestToken).success
    : campaignDraftRequestTokenSchema.safeParse(requestToken).success;
  const serializedMutation = JSON.stringify(parsedMutation.data);
  if (
    !requestTokenValid
    || utf8ByteLength(serializedMutation) > MAX_MULTI_ANSWER_REQUEST_BYTES
  ) throw new CampaignAccessApiError();
  const expiryTime = new Date(String(sessionExpiresAt || "")).getTime();
  if (!Number.isFinite(expiryTime)) throw new CampaignAccessApiError();
  const result = await requestCampaignAccess(
    "/campaign-access/questionnaire/draft",
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-Campaign-Draft-Token": requestToken,
      },
      body: serializedMutation,
      ...(signal ? { signal } : {}),
    },
    anyDraftMutationResultSchema,
  );
  const expectedState = parsedMutation.data.action === "save" ? "saved" : "cleared";
  const changedTime = new Date(result.changedAt).getTime();
  if (
    (v2
      ? result.schemaVersion !== "campaign_uat_questionnaire_draft_mutation_result.v2"
        || result.answerSetRef !== parsedMutation.data.answerSetRef
        || result.answerCount !== (parsedMutation.data.action === "save"
          ? parsedMutation.data.answers.length
          : 0)
      : result.schemaVersion !== "campaign_uat_questionnaire_draft_mutation_result.v1")
    ||
    result.state !== expectedState
    || result.revision !== parsedMutation.data.expectedRevision + 1
    || result.expiresAt !== sessionExpiresAt
    || !Number.isFinite(changedTime)
    || changedTime >= expiryTime
  ) throw new CampaignAccessApiError();
  return result;
};

export const submitCampaignQuestionnaireResponseRehearsal = async ({
  requestToken,
  mutation,
  sessionExpiresAt,
  signal,
}) => {
  const parsedMutation = anyResponseSubmitMutationSchema.safeParse(mutation);
  if (!parsedMutation.success) throw new CampaignAccessApiError();
  const v2 = parsedMutation.data.schemaVersion
    === "campaign_uat_questionnaire_response_submit.v2";
  const requestTokenValid = v2
    ? campaignDraftRequestTokenV2Schema.safeParse(requestToken).success
    : campaignDraftRequestTokenSchema.safeParse(requestToken).success;
  const serializedMutation = JSON.stringify(parsedMutation.data);
  if (
    !requestTokenValid
    || utf8ByteLength(serializedMutation) > MAX_MULTI_ANSWER_REQUEST_BYTES
  ) throw new CampaignAccessApiError();
  const expiryTime = new Date(String(sessionExpiresAt || "")).getTime();
  if (!Number.isFinite(expiryTime) || expiryTime <= Date.now()) {
    throw new CampaignAccessApiError();
  }
  const result = await requestCampaignAccess(
    "/campaign-access/questionnaire/response-rehearsal/submit",
    {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "X-Campaign-Draft-Token": requestToken,
      },
      body: serializedMutation,
      ...(signal ? { signal } : {}),
    },
    anyResponseSubmitResultSchema,
  );
  const submittedAt = new Date(result.submittedAt).getTime();
  const reviewedAt = result.reviewedAt === null
    ? null
    : new Date(result.reviewedAt).getTime();
  if (
    (v2
      ? result.schemaVersion !== "campaign_uat_questionnaire_response_receipt.v2"
        || result.answerSetRef !== parsedMutation.data.answerSetRef
      : result.schemaVersion !== "campaign_uat_questionnaire_response_receipt.v1")
    ||
    submittedAt >= expiryTime
    || (reviewedAt !== null && reviewedAt >= expiryTime)
  ) throw new CampaignAccessApiError();
  return result;
};

export const CAMPAIGN_ACCESS_PUBLIC_MESSAGES = Object.freeze({
  unavailable: DEFAULT_PUBLIC_ERROR,
  invalid: INVALID_PUBLIC_ERROR,
  rateLimited: RATE_LIMIT_PUBLIC_ERROR,
  codeInvalid: CODE_INVALID_PUBLIC_ERROR,
  challengeExpired: CHALLENGE_EXPIRED_PUBLIC_ERROR,
  challengeLocked: CHALLENGE_LOCKED_PUBLIC_ERROR,
  deliveryDisabled: DELIVERY_DISABLED_PUBLIC_ERROR,
  draftConflict: DRAFT_CONFLICT_PUBLIC_ERROR,
  draftClear: DRAFT_CLEAR_PUBLIC_ERROR,
  draftValidation: DRAFT_VALIDATION_PUBLIC_ERROR,
});
